<template>
  <section>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="4">
            <e-store-combo
              v-model="filters.storeId"
              :required="false"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="invoiceNumber"
              v-model="filters.invoiceNumber"
              type="text"
              :label="$t('Número da NF')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="invoiceStatus"
              v-model="filters.status"
              :placeholder="$t('Status')"
              type="vue-select"
              :label="$t('Status da Nota')"
              :options="statusInvoiceOptions()"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="sale-id"
              v-model="filters.saleId"
              type="number"
              :label="$t('Id da Venda')"
              :placeholder="$t('Id da Venda')"
              :instructions="[
                { text: $t('Auxilia na busca de NFe que possui alguma referência com venda. Ex:') },
                { text: $t('NFe que foi gerada a partir de cupom fiscal') },
                { text: $t('NFe que criou venda') },
                { text: $t('NFe de cancelamento de venda fora do prazo da SEFAZ') },
              ]"
            />
          </b-col>

          <b-col md="4">
            <FormulateInput
              id="issueDate"
              v-model="filters.issueDate"
              type="date-range-picker"
              :label="$t('Período de emissão')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="movementDate"
              v-model="filters.movementDate"
              type="date-range-picker"
              :label="$t('Período de movimentação')"
            />
          </b-col>
          <b-col md="4">
            <e-person-search
              v-model="filters.supplier"
              :label="$t('Pesquisar fornecedor')"
              is-supplier
              :only-active="false"
              value-is-object
            />
          </b-col>
          <b-col md="4">
            <e-person-search
              v-model="filters.customer"
              :label="$t('Pesquisar cliente')"
              is-customer
              :only-active="false"
              value-is-object
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="nfeAccessKey"
              v-model="filters.nfeAccessKey"
              type="text"
              :label="$t('Chave da NFe')"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="invoice-invoice-natureOperation"
              v-model="filters.cfop"
              :type="'vue-select'"
              :options="getComboCfopNatureOperation"
              :label="$t('Código CFOP')"
              :instruction="$t('Filtra notas que contém algum item com o CFOP informado')"
              @input="onSelectNatureOperation"
            />
          </b-col>
          <b-col md="4">
            <FormulateInput
              id="loss_reason"
              v-model="filters.stockLossesReason"
              :label="$t('Motivo da baixa de estoque')"
              type="vue-select"
              :placeholder="$t('Selecione')"
              :options="invoiceStockLossesReasonOptions()"
              :instruction="$t('Filtra notas que contém algum item com o motivo informado')"
            />
          </b-col>
        </b-row>
      </div>

      <template #after_buttons>
        <e-button
          class="ml-1"
          :busy="downloading"
          :text="$t('Baixar Relatório')"
          variant="primary"
          icon="download"
          @click="onDownloadReport"
        />
      </template>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        ref="invoices-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('INVOICE.NAME'), 'female', queryExecuted)"
        :fields="fields"
        :items="invoices"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(action)="row">
          <e-grid-actions
            :show-update="false"
            :show-delete="false"
            :buttons="gridButtons(row)"
            @edit="onUpdateInvoice(row)"
            @consult-invoice="onConsultInvoice(row)"
            @download-pdf="onDownloadPdf(row)"
            @download-xml="onDownloadXml(row)"
          />
        </template>
        <template #head(status)="{ label }">
          <span>
            <e-instructions
              target="instruction_status"
              container-class="mr-0"
              :instructions="[
                { text: $t('INVOICE.TABLE_INSTRUCTION.STATUS_1') },
                { text: $t('INVOICE.TABLE_INSTRUCTION.STATUS_2') },
                { text: $t('INVOICE.TABLE_INSTRUCTION.STATUS_3') },
              ]"
            />
            {{ label.toUpperCase() }}
          </span>
        </template>
        <template #head(invoiceNumber)="{ label }">
          <span>
            <e-instructions
              target="instruction_invoice_number"
              container-class="mr-0"
              :instructions="[
                { text: $t('INVOICE.TABLE_INSTRUCTION.INVOICE_NUMBER_1') },
                { text: $t('INVOICE.TABLE_INSTRUCTION.INVOICE_NUMBER_2') },
              ]"
            />
            {{ label.toUpperCase() }}
          </span>
        </template>
      </b-table>

      <b-row class="pt-1">
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="invoices-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <fab
      v-if="$can('Create', 'Invoice')"
      :main-tooltip="$t('Adicionar Nota Fiscal')"
      main-icon="keyboard_command_key"
      :actions="actions"
      :fixed-tooltip="true"
      @create-with-import="onCreateInvoiceFromCoupon"
      @create-manual="onCreateInvoice"
    />

    <consult-invoice-modal ref="consultInvoiceModal" />
  </section>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EStoreCombo,
  EInstructions,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { downloader, invoiceDomains, statusTypes } from '@/mixins'
import { mapActions, mapState, mapGetters } from 'vuex'
import EFilters from '@/views/components/EFilters.vue'
import EGridActions from '@/views/components/EGridActions.vue'
import EPersonSearch from '@/views/components/inputs/EPersonSearch.vue'
import EButton from '@/views/components/EButton.vue'
import ConsultInvoiceModal from './components/ConsultInvoiceModal.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPageSizeSelector,
    EPaginationSummary,
    fab,
    EFilters,
    EGridActions,
    EStoreCombo,
    EPersonSearch,
    ConsultInvoiceModal,
    EButton,
    EInstructions,
  },

  mixins: [statusTypes, invoiceDomains, downloader],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
      edit: false,
      saving: false,
      downloading: false,
    }
  },

  computed: {
    ...mapState('pages/invoice/invoice', ['invoices', 'paging', 'sorting', 'filters']),
    ...mapGetters('pages/invoice/taxClassifications', [
      'getComboCfopNatureOperation',
      'getCfopNatureOperation'
    ]),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Número'),
          key: 'invoiceNumber',
          tdClass: 'text-right',
          thStyle: { width: '120px' },
          sortable: true,
        },
        {
          label: this.$t('Loja'),
          key: 'store.id',
          tdClass: 'text-left',
          thStyle: { width: '280px' },
          formatter: (val, index, item) => this.$options.filters.storeName(item.store),
          sortable: true,
        },
        {
          label: this.$t('Fornecedor/Cliente'),
          key: 'customerOrSupplier',
          tdClass: 'text-left',
          formatter: (val, index, item) => {
            if (item.supplier) return item.supplier.companyName || item.supplier.name
            if (item.customer) return item.customer.companyName || item.customer.name
            return '-'
          },
        },
        {
          label: this.$t('Valor'),
          key: 'totalValue',
          tdClass: 'text-right',
          thStyle: { width: '120px' },
          formatter: value => this.$options.filters.currency(value),
          sortable: true,
        },
        {
          label: this.$t('Data Emissão'),
          key: 'issueDate',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          formatter: value => this.$options.filters.date(value),
          sortable: true,
        },
        {
          label: this.$t('Data Movimen.'),
          key: 'movimentDate',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          formatter: value => this.$options.filters.date(value),
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'status',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '120px' },
          formatter: value => this.statusInvoiceLabel[value] || '-',
          sortable: true,
        },
      ]
    },

    actions() {
      return [
        {
          name: 'create-with-import',
          icon: 'note_add',
          color: 'green',
          tooltip: this.$t('Importando Cupom Fiscal'),
        },
        {
          name: 'create-manual',
          icon: 'add',
          color: 'green',
          tooltip: this.$t('Manual'),
        },
      ]
    },
  },

  mounted() {
    this.fetchTaxClassificationCombos()
  },

  methods: {
    ...mapActions('pages/invoice/taxClassifications', ['fetchTaxClassificationCombos']),
    ...mapActions('pages/invoice/invoice', [
      'setFilter',
      'setCurrentPage',
      'setPageSize',
      'fetchInvoices',
      'resetFilters',
      'downloadReport',
    ]),
    ...mapActions('pages/invoice/invoice/invoiceMaintain', {
      stCleanInvoiceMaintain: 'cleanState',
      stInvoiceManage: 'invoiceManage',
      stInvoiceDownload: 'invoiceDownload',
      cleanSaleInvoices: 'cleanSaleInvoices',
    }),

    onSelectNatureOperation(val) {
      if (!val) {
        this.$emit('clean-nature-operation')
        return
      }
      if (val.includes('-')) {
        for (let i = 0; i < this.getCfopNatureOperation.length; i += 1) {
          if (this.getCfopNatureOperation[i].code === Number(val.split('-')[0].trim())) {
            this.$emit('set-nature-operation', this.getCfopNatureOperation[i])
          }
        }
      }
    },

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchInvoices()
          this.queryExecuted = true
        } catch (error) {
          this.showError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },

    filterData() {
      this.filters.nfeAccessKey = this.filters.nfeAccessKey.replace(/\s/g, '')

      this.setCurrentPage(1)
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    onCreateInvoice() {
      this.stCleanInvoiceMaintain()
      this.$router.push({ name: 'invoice-create' })
    },

    onCreateInvoiceFromCoupon() {
      this.stCleanInvoiceMaintain()
      this.cleanSaleInvoices()
      this.$router.push({ name: 'invoice-create-from-coupon' })
    },

    async onUpdateInvoice(row) {
      const { id } = row.item
      this.$router.push({ name: 'invoice-maintain', params: { id } })
    },
    async onConsultInvoice(row) {
      await this.$refs.consultInvoiceModal.show(row.item)
    },
    async onDownloadPdf(row) {
      try {
        this.fetching = true

        const ret = await this.stInvoiceDownload({
          pathUrl: row.item.nfeDetail?.danfeNFePath,
        })

        if (ret.responsePayload && ret.responsePayload !== '') {
          const linkSource = `data:application/pdf;base64,${ret.responsePayload}`
          const downloadLink = document.createElement('a')
          const fileName = `${row.item.nfeDetail?.accessKey}.pdf`

          downloadLink.href = linkSource
          downloadLink.download = fileName
          downloadLink.click()
          this.showSuccess({ message: this.$t('Sucesso.') })
        } else {
          this.showError('Ocorreu um erro ao imprimir a NFe!')
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },
    async onDownloadXml(row) {
      try {
        this.fetching = true

        const ret = await this.stInvoiceDownload({
          pathUrl: row.item.nfeDetail?.xmlFinishedSuccessPath,
        })

        if (ret.responsePayload && ret.responsePayload !== '') {
          const linkSource = `data:application/pdf;base64,${ret.responsePayload}`
          const downloadLink = document.createElement('a')
          const fileName = `${row.item.nfeDetail?.accessKey}.xml`

          downloadLink.href = linkSource
          downloadLink.download = fileName
          downloadLink.click()
          this.showSuccess({ message: this.$t('Sucesso.') })
        } else {
          this.showError('Ocorreu um erro ao imprimir a NFe!')
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },
    async onDownloadReport() {
      try {
        this.downloading = true

        const blobData = await this.downloadReport()

        if (blobData) {
          this.forceFileDownloadXlsx(blobData, this.$t('Relatorio_Nota_Fiscal'))
          this.showSuccess({ message: this.$t('Sucesso') })
        } else {
          this.showError('Ocorreu um erro ao realizar o download do relatório de Nota fiscal')
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.downloading = false
      }
    },

    gridButtons(row) {
      const { SUCCESS_FINISHED, CANCELED, INVALIDATED, DENIED, SEQUENCE_SKIPPED } =
        this.statusInvoiceEnum
      const isInvalidated = row.item.status === INVALIDATED || row.item.status === SEQUENCE_SKIPPED
      const isBlockedBySefazRejection = row.item.nfeDetail?.returnCode
        === this.sefazReturnCodeEnum.DUPLICATE_NFE_DIFFERENT_ACCESS_KEY

      const canConsultInvoice =
        row.item.status === SUCCESS_FINISHED ||
        row.item.status === CANCELED ||
        isInvalidated ||
        row.item.status === DENIED ||
        isBlockedBySefazRejection

      // TODO permissão
      let btns = []
      if (canConsultInvoice) {
        btns = [
          {
            icon: 'search',
            variant: 'primary',
            title: this.$t('Consultar Status da Nota'),
            event: 'consult-invoice',
            hide: isInvalidated || isBlockedBySefazRejection,
          },
          {
            icon: 'eye-fill',
            variant: 'primary',
            title: this.$t('Visualizar Nota'),
            event: 'edit',
          },
          {
            icon: 'printer-fill',
            variant: 'primary',
            title: this.$t('Download do PDF'),
            event: 'download-pdf',
            hide: isInvalidated
              || row.item.status === DENIED
              || isBlockedBySefazRejection,
          },
          {
            icon: 'download',
            variant: 'primary',
            title: this.$t('Download do XML'),
            event: 'download-xml',
            hide: isInvalidated || isBlockedBySefazRejection,
          },
        ]
      } else {
        btns = [
          {
            icon: 'pencil',
            variant: 'primary',
            title: this.$t('Editar'),
            event: 'edit',
          },
        ]
      }

      return btns.filter(btn => !btn.hide)
    },
  },
}
</script>

<style></style>
